<template>
  <floating-element
    class="chart-hover-legend"
    :x="x"
    :y="y"
    :x-offset="15"
    transition-name="none"
  >
    <header class="chart-hover-legend__header">
      <h5 v-if="parentItem" class="chart-hover-legend__parent">
        {{ parentItem.title }}
      </h5>
      <span
        v-if="item.type != 'simulationRound'"
        class="chart-hover-legend__type"
      >
        {{ type }}
      </span>
      <h2 class="chart-hover-legend__title">{{ title }}</h2>
    </header>
    <div class="chart-hover-legend__body">
      <template v-if="'value' in item">
        <div class="chart-hover-legend__value">
          {{ formatNumber(item.value, item?.numberFormat || "value") }} <span
            class="chart-hover-legend__unit"
            >{{ item?.unit || "" }}</span
          >
        </div>
      </template>
    </div>
    <footer
      v-if="
        'startValue' in item &&
        item.startValue != null &&
        'value' in item &&
        item.value != item.startValue
      "
      class="chart-hover-legend__footer"
    >
      <div class="chart-hover-legend__subvalues">
        <span
          >{{
            yAxis == "effect"
              ? $t("labels.directEffect")
              : $t("labels.startValue")
          }}:</span
        >
        <span>{{ formatNumber(item.startValue, item?.numberFormat || "decimal") }}</span>
        <span
          >{{
            yAxis == "effect"
              ? $t("labels.indirectEffect")
              : $t("chartAxes.gain")
          }}:</span
        >
        <span>
          <!--{{ Math.sign(item.value - item.startValue) < 0 ? "-" : "+" }}-->
          {{ formatNumber((item.value || 0) - item.startValue, "points") }}</span
        >
      </div>
    </footer>
  </floating-element>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { ensureInt } from "@/helpers/utility/Type";
import { isStoreItemOfType } from "@editor/items/ItemOfStore";
import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";

import FloatingElement from "@core/ui/FloatingElement.vue";

import { useFlowStore } from "@editor/items/flow/flows.store";
import { useModelStore } from "@editor/items/model/model.store";

import type { DataEntry, EntryValue } from "@editor/items/charts/ChartData";
import type { ScheduledEffectItem } from "@editor/items/scheduledEffect/ScheduledEffectItem";
import type { StoreItem } from "@editor/items/StoreItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";
import type { ChartSettings } from "./ChartItem";

const props = withDefaults(defineProps<{
  item: EntryValue;
  x: number;
  y: number;
  parentItem?: DataEntry;
  yAxis?: ChartSettings["yAxis"];
}>(), {
  yAxis: "value"
});

const { t, n } = useI18n();
const modelStore = useModelStore();

const simulation = computed(() => {
  if (props.item.simulationId != null) {
    return modelStore.getItem(
      "simulation",
      ensureInt(props.item.simulationId)
    );
  }
  return null;
});

const type = computed(() => {
  if (props.item.type == "simulationRound") {
    const roundUnit = simulation.value?.roundUnit || "round";
    return  t(`roundUnits.${roundUnit}`);
  }
  if (props.item.type == "flowBranch") {
    return t("objectNames.flow");
  }
  return t(`objectNames.${props.item.type}`);
});

const storeItem = computed(() => {
  let type = props.item.type;
  let id = props.item.id;
  if (type == "simulationRound") {
    if (props.item.roundIndex == null) return null;
    return simulation.value?.rounds?.[props.item.roundIndex];
  }
  if (type == "delayed_action" || type == "delayed_incident") {
    type = unDelayType(type) as "action" | "incident";
  }
  if (type == "scheduledEffect") {
    const sim = simulation.value;
    if (!sim) return null;
    if (props.item.roundIndex == null) return null;
    const roundIndex = props.item.roundIndex;
    const round = sim.rounds[roundIndex];
    if (round == null) return null;
    const actionPlan = sim.actionPlans[roundIndex];
    if (actionPlan == null) return null;
    const effect = actionPlan.scheduledEffects.find(
      (e) => e.id == props.item.id
    );
    return effect || null;
  }
  if (
    type == "actionActivation" ||
    type == "incidentActivation" ||
    type == "scheduledEffectActivation"
  ) {
    return null;
  }
  const item = modelStore.getItem(type, ensureInt(id));
  if (item == null) return null;
  return item;
});

const title = computed(() => {
  if (props.item.type == "simulationRound") {
    return formatRoundIndex(props.item?.roundIndex || 0);
  }
  if (props.item.type == "momentum") {
    return t("objectNames.momentum");
  }
  if (storeItem.value) {
    if (isFlowBranch(storeItem.value)) {
      return useFlowStore().titles[storeItem.value.flowId];
    }
    if (isScheduledEffectItem(storeItem.value)) {
      const affectedItem = modelStore.getItem(
        storeItem.value.affectedType,
        storeItem.value.affectedId
      );
      return (
        t("objectNames.scheduledEffect") +
        " → " +
        affectedItem?.title +
        ` (${formatRoundIndex(props.item.roundIndex)})`
      );
    }
    if (isStoreItemOfType(storeItem.value, "relation")) {
      const sourceItem = modelStore.getItem(
        storeItem.value.startItemType,
        storeItem.value.startItemId
      );
      return sourceItem?.title + " →";
    }
    if ("title" in storeItem.value) {
      return storeItem.value?.title;
    }
  }
  if ("title" in props.item) return props.item?.title;
  return "";
});

// ======================
// Formatting functions

function formatRoundIndex(roundIndex?: number) {
  if (roundIndex == null) return "";
  return formatRoundName(
    simulation.value?.durationType || "roundAmount",
    simulation.value?.roundUnit || "round",
    simulation.value?.roundLength ?? 1,
    roundIndex,
    simulation.value?.timeStart || null
  );
}

function formatNumber(value: number | null | undefined, format: string) {
  if (value == null) return "";
  if (isNaN(value)) return "?";
  if (value == Infinity) return "∞";
  if (value == -Infinity) return "∞";
  return n(value, format);
}

// ======================
// Type guards and helpers

function isScheduledEffectItem(
  item: ScheduledEffectItem | StoreItem
): item is ScheduledEffectItem {
  return item.type == "scheduledEffect";
}
function isFlowBranch(item: { type: string }): item is FlowBranchItem {
  return item.type == "flowBranch";
}
function unDelayType<R extends string, K extends `delayed_${R}`>(type: K) {
  return type.replace("delayed_", "") as R;
}
</script>

<style lang="scss">
.chart-hover-legend {
  position: fixed;
  top: 0;
  left: 0;
  background-color: var(--color-background-close);
  border-radius: 1rem;
  border-top-left-radius: 0;
  width: 15rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  pointer-events: none;

  &__header {
    padding: 1rem;
    padding-top: 0.75rem;
    padding-bottom: 0.5rem;
    background-color: var(--color-background);
    border-bottom: 1px solid var(--color-border);
  }

  &__body {
    padding: 1rem;
    padding-top: 0.5rem;
  }

  &__footer {
    background-color: var(--color-background);
    padding: 0.5rem 1rem;
  }

  &__parent {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: var(--color-text-medium);
  }
  &__type {
    font-size: 12px;
  }
  &__title {
    margin-top: 0.25em;
    font-size: 1rem;
    hyphens: auto;
    margin-bottom: 0.25rem;
    color: var(--color-text-moderate);
  }

  &__value {
    font-size: 1.25rem;
    font-weight: 600;
  }
  &__unit {
    font-size: 1rem;
  }

  &__subvalues {
    font-size: 1rem;
    color: var(--color-text-medium);
    display: grid;
    grid-template-columns: 1fr max-content;
    column-gap: 0.25rem;

    & > span:nth-child(2n) {
      text-align: right;
    }
  }
}
</style>
